import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import cn from 'classnames';
import { produce } from 'immer';

import { Button, Icons, SpinnerOverlay, SubmissionButtons } from '@components/common';

import { uuid } from '@utils/common';

import { IBasicNode } from '@interfaces/components/tree';

import {
	ARRAY_NODE_KEY,
	BRACKETS_OFFSET,
	FLAT_NODE_PATH_SEPARATOR,
	FLAT_TREE_NODE_VIRTUALIZED_DEFAULT_HEIGHT as virtualizationHeight,
} from './constants';

import { IAddXMLNode } from '../FlatXMLTree/interface';
import FlatTreeNodeWrapper from './FlatTreeNodeWrapper';
import { IFlatTreeNodeInterface, TFlatTreeGetValidation } from './interface';
import {
	createObjectFromFlatArray,
	getLastNotCollapsedIndex,
	getPathWithoutParents,
	toggleFlatTree,
} from './utils';

import styles from './FlatTree.module.scss';

const COPY_TIMEOUT = 2000;

export type TFlatTreeSubmitHandler = (data: IFlatTreeNodeInterface[]) => void;

interface IFlatTreeProps {
	treeData: IFlatTreeNodeInterface[];
	isReadOnly?: boolean;
	submitHandler?: TFlatTreeSubmitHandler;
	cancelHandler?: () => void;
	wrapperClassName?: string;
	getValidationRules?: TFlatTreeGetValidation;
	isLoading?: boolean;
	isCloseButtonOnly?: boolean;
	isCopyButtonNeeded?: boolean;
	rawData?: string;
	isWithVirtualization?: boolean;
}

const FlatTree = ({
	treeData,
	isReadOnly = false,
	submitHandler,
	cancelHandler,
	wrapperClassName,
	getValidationRules,
	isLoading,
	isCloseButtonOnly,
	isCopyButtonNeeded,
	rawData,
	isWithVirtualization,
}: IFlatTreeProps) => {
	const [data, setData] = useState<IFlatTreeNodeInterface[]>(treeData);
	const [checkedNodes, setCheckedNodes] = useState<string[]>(() => {
		return treeData
			.filter((node) => node.isChecked && !node.nodeType)
			.map((node) => node.id);
	});

	const [isCopied, setIsCopied] = useState(false);

	const handleCopyClick = useCallback(() => {
		const formattedJSON = JSON.stringify(
			typeof rawData === 'string' ? JSON.parse(rawData) : rawData,
			null,
			2,
		);

		navigator.clipboard
			.writeText(formattedJSON)
			.then(() => {
				setIsCopied(true);
				setTimeout(() => setIsCopied(false), COPY_TIMEOUT);
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('Failed to copy: ', err);
			});
	}, [rawData]);

	useEffect(() => {
		setData(treeData);
	}, [treeData]);

	const copyObjectToClipboard = useCallback((node: IFlatTreeNodeInterface) => {
		setData(
			produce((draftState) => {
				const { id, keysPath: parentPath, key: parentKey } = node;

				const parentIndex = draftState.findIndex((item) => item.id === id);

				if (parentIndex < 0) return;

				const nodesToCopy = [];

				for (let index = parentIndex; index < draftState.length; index++) {
					const isCommonRoot = draftState[index].keysPath?.startsWith(parentPath + FLAT_NODE_PATH_SEPARATOR)
						|| draftState[index].keysPath === parentPath;

					if (isCommonRoot) {
						nodesToCopy.push(draftState[index]);
					} else {
						break;
					}
				}

				const pathToIgnore = getPathWithoutParents(parentPath!, parentKey);

				const obj = createObjectFromFlatArray(nodesToCopy, pathToIgnore).result;

				void navigator.clipboard.writeText(JSON.stringify(obj));
			}),
		);
	}, []);

	const removeNodeHandler = useCallback((node: IFlatTreeNodeInterface) => {
		setData(
			produce((draftState) => {
				const { id } = node;

				for (let index = 0; index < draftState.length; index++) {
					//find node with id we need to remove
					if (draftState[index].id === id) {
						//get index of the last not collapsed node, to pass there brackets if they exist
						const lastNotCollapsedIndex = getLastNotCollapsedIndex(draftState, index - 1);

						//if node has brackets, add them to the last not collapsed node
						if (lastNotCollapsedIndex !== undefined && node.brackets) {
							const brackets = draftState[lastNotCollapsedIndex].brackets || [];

							draftState[lastNotCollapsedIndex].brackets = [...brackets, ...node.brackets];
						}

						//remove node from the array
						draftState.splice(index, 1);

						break;
					}
				}
			}),
		);

		setCheckedNodes((prevState) => prevState.filter((id) => id !== node.id));
	}, []);

	const collapseNodesHandler = useCallback((id: string, parentPath: string) => {
		setData(
			produce((draftState) => {
				toggleFlatTree(draftState, id, parentPath);
			}),
		);
	}, []);

	const editNodeHandler = useCallback((id: string, value: string) => {
		setData(
			produce((draftState) => {
				for (let index = 0; index < draftState.length; index++) {
					if (draftState[index].id === id) {
						draftState[index].value = value;

						break;
					}
				}
			}),
		);
	}, []);

	const skipNodeHandler = useCallback((id: string) => {
		setData(
			produce((draftState) => {
				for (let index = 0; index < draftState.length; index++) {
					if (draftState[index].id === id) {
						const nodeChecked = draftState[index].isChecked;

						draftState[index].isChecked = !nodeChecked;

						setCheckedNodes((prevState) => {
							if (!nodeChecked) {
								return [...prevState, id];
							}

							return prevState.filter((nodeId) => nodeId !== id);
						});

						break;
					}
				}
			}),
		);
	}, []);

	const submitTreeData = () => {
		if (submitHandler) submitHandler(data);
	};

	const cancelTreeData = useCallback(() => {
		if (cancelHandler) cancelHandler();
	}, [cancelHandler]);

	const getChildrenNamesList = useCallback((node: IFlatTreeNodeInterface) => {
		const regex = new RegExp('^' + node.keysPath + '/[^/]+$');

		return data
			.filter((item) => item?.keysPath && regex.test(item.keysPath))
			.map((item) => item.key);
	}, [data]);

	const addNodeHandler = useCallback(({
		parent,
		value,
		key,
	}: IAddXMLNode<IBasicNode>) => {
		setData(produce((draftState) => {
			const {
				path,
				isHiddenChildren,
				depth,
				id,
				brackets,
				description,
				keysPath,
			} = parent;

			for (let index = 0; index < draftState.length; index++) {
				const node = draftState[index];

				if (node.id === id) {
					//set string for a key if it is array's node,
					//to avoid recalculate keys for all array's children
					const nodeKey = key ? key : ARRAY_NODE_KEY;

					if (key === node.key) {
						//if we pass same key as existing one, we have to update value
						draftState[index] = { ...node, value };
					} else {
						draftState.splice(index + 1, 0, {
							key: nodeKey,
							value,
							nodeType: undefined,
							isChecked: true,
							description,
							depth: depth + 1,
							isHiddenChildren: false,
							id: uuid(),
							isCollapsed: Boolean(isHiddenChildren),
							path: `${path}${FLAT_NODE_PATH_SEPARATOR}${nodeKey}`,
							keysPath: `${keysPath}${FLAT_NODE_PATH_SEPARATOR}${nodeKey}`,
							//if parent expanded and its first added child, we have to add brackets to it
							brackets: !isHiddenChildren ? brackets : undefined,
						});

						if (brackets) {
							//if node is collapsed, we have to add brackets to it
							node.brackets = isHiddenChildren ? brackets : undefined;
						}
					}

					break;
				}
			}

			setCheckedNodes((prevState) => [...prevState, id]);
		}));
	}, []);

	const filteredData = useMemo(() => data.filter((node) => node.isHidden ? false : !node.isCollapsed), [data]);
	const listRef = useRef();

	useEffect(() => {
		if (listRef.current) {
			(listRef.current as any).resetAfterIndex(0);
		}
	}, [data]);

	return (
		<div className={cn(styles.treeWrapper, wrapperClassName)}>
			<div className={styles.treeFrame}>
				{isLoading && <SpinnerOverlay />}
				{isWithVirtualization ? (
						<AutoSizer>
							{({ height = 0, width = 0 }) => (
								<List
									ref={listRef as any}
									height={height}
									itemCount={filteredData.length}
									itemData={filteredData}
									itemSize={(index: number) => {
										const countOfBrackets = filteredData[index].brackets?.length;
										return countOfBrackets
											? virtualizationHeight * (countOfBrackets) + BRACKETS_OFFSET
											: virtualizationHeight;
									}}
									width={width}
								>
									{({ data, style, index }: any) => {
										return (
											<FlatTreeNodeWrapper
												key={`node_${data[index].id}_${index}`}
												getChildrenNamesList={getChildrenNamesList}
												getValidationRules={getValidationRules}
												index={data[index].id}
												isReadOnly={isReadOnly}
												isWithVirtualization
												node={data[index]}
												onAdd={addNodeHandler}
												onCollapse={collapseNodesHandler}
												onCopy={copyObjectToClipboard}
												onEdit={editNodeHandler}
												onRemove={removeNodeHandler}
												onSkip={skipNodeHandler}
												style={style}
											/>
										);
									}}
								</List>
							)}
						</AutoSizer>
					)
					: data.filter((node) => !node.isCollapsed)
						.map((node) => {
							return (
								<FlatTreeNodeWrapper
									key={`node_${node.id}`}
									getChildrenNamesList={getChildrenNamesList}
									getValidationRules={getValidationRules}
									index={node.id}
									isReadOnly={isReadOnly}
									node={node}
									onAdd={addNodeHandler}
									onCollapse={collapseNodesHandler}
									onCopy={copyObjectToClipboard}
									onEdit={editNodeHandler}
									onRemove={removeNodeHandler}
									onSkip={skipNodeHandler}
								/>
							);
						})
				}
			</div>
			{!isReadOnly &&
				<SubmissionButtons
					disabledPositive={!checkedNodes.length}
					negativeHandler={cancelTreeData}
					negativeLabel='Cancel'
					positiveHandler={submitTreeData}
					positiveLabel='Confirm'
				/>
			}
			{((isReadOnly && isCloseButtonOnly) || isCopyButtonNeeded) && (
				<div
					className={styles.actionsButtonContainer}
					onClick={handleCopyClick}
					role='button'
				>
					{isCopyButtonNeeded && (
						<div className={cn(styles.copyButtonContainer, {
							[styles.copiedState]: isCopied,
						})}>
							{isCopied ? Icons.checkMark() : Icons.copy()}
							<span>
								{isCopied ? 'Copied' : 'Copy'}
							</span>
						</div>
					)}
					{isReadOnly && isCloseButtonOnly && (
						<Button
							onClick={cancelTreeData}
							text='Close'
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default memo(FlatTree);
